
import React, { useMemo } from "react";
import { useTournament } from "@/hooks/use-tournament";
import { useLanguage } from "@/hooks/use-language";
import { Button } from "@/components/ui/button";
import { SharedResultsEntry } from "./shared/SharedResultsEntry";
import { AmericanoLeaderboard } from "./americano/AmericanoLeaderboard";
import { AmericanoAdapter } from "@/adapters/tournament-adapters";
import { RoundHistoryDialog } from "./RoundHistoryDialog";
import { TournamentHeader } from "./shared/TournamentHeader";
import { BenchPanel } from "./shared/BenchPanel";
import { AmericanoBenchDisplay } from "./americano/BenchDisplay";

interface AmericanoTournamentProps {
  participants: any[];
  onBack?: () => void;
}

export const AmericanoTournament: React.FC<AmericanoTournamentProps> = ({ participants, onBack }) => {
  const { language } = useLanguage();
  const { 
    tournamentState, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    goToPreviousRound,
    finishAmericanoRound
  } = useTournament();

  // Create adapter for shared component using useMemo to ensure it updates when tournament state changes
  const americanoAdapter = useMemo(() => 
    new AmericanoAdapter(tournamentState, finishAmericanoRound), 
    [tournamentState, finishAmericanoRound]
  );

  // Ensure leaderboard renders whenever the tournament is finished even if the round flag lingers
  const shouldShowLeaderboard = tournamentState.isFinished || showLeaderboard;
  const showBackButton = !shouldShowLeaderboard && (tournamentState.isRoundActive || tournamentState.isFinished);
  const [showHistory, setShowHistory] = React.useState(false);

  // Log rendering decisions
  React.useEffect(() => {
    console.log('🎨 AmericanoTournament render state:', {
      showLeaderboard,
      shouldShowLeaderboard,
      isRoundActive: tournamentState.isRoundActive,
      isFinished: tournamentState.isFinished,
      isActive: tournamentState.isActive,
      currentRound: tournamentState.currentRound,
      totalRounds: tournamentState.totalRounds,
      willShowLeaderboard: shouldShowLeaderboard || !tournamentState.isRoundActive
    });
  }, [
    showLeaderboard,
    shouldShowLeaderboard,
    tournamentState.isRoundActive,
    tournamentState.isFinished,
    tournamentState.isActive,
    tournamentState.currentRound,
    tournamentState.totalRounds
  ]);

  const handleBack = () => {
    // If we're in round 1, go back to front page
    if (tournamentState.currentRound === 1) {
      if (onBack) {
        onBack();
      } else {
        resetTournament();
      }
    } else {
      // If we're in round 2+, go to previous round
      if (goToPreviousRound) {
        goToPreviousRound();
      }
    }
  };

  return (
    <div className="mt-8 border-t pt-6">
      <div className="space-y-6">
        <TournamentHeader
          title={tournamentState.isFinished
            ? (language === 'no' ? 'Americano Turnering Ferdig' : 'Americano Tournament Finished')
            : (language === 'no' ? `Americano Runde ${tournamentState.currentRound}` : `Americano Round ${tournamentState.currentRound}`)}
          language={language}
          showBack={showBackButton}
          onBack={handleBack}
          showLeaderboard={shouldShowLeaderboard}
          onToggleLeaderboard={() => {
            if (tournamentState.isFinished) return;
            setShowLeaderboard(!showLeaderboard);
          }}
          onHistory={() => setShowHistory(true)}
        />

        {/* Show bench players once (Americano-specific display) */}
        {tournamentState.americanoPlayers.some(p => p.isOnBench) && (
          <AmericanoBenchDisplay />
        )}
        {shouldShowLeaderboard ? (
          <AmericanoLeaderboard />
        ) : tournamentState.isRoundActive ? (
          <SharedResultsEntry 
            adapter={americanoAdapter}
            currentRound={tournamentState.currentRound}
            totalRounds={tournamentState.totalRounds}
          />
        ) : (
          <AmericanoLeaderboard />
        )}
      </div>
      <RoundHistoryDialog open={showHistory} onOpenChange={setShowHistory} />
    </div>
  );
};
